import { FC, useRef, useState } from "react";

import { useForm } from "react-hook-form";

import { RecaptchaAction } from "../../../enums/recaptcha";
import { useRecaptchaRetry } from "../../../hooks/useRecaptchaRetry";
import { NewsletterFormSignupData } from "../Editor/types";

import { NewsletterFormSignUpStateless } from "./NewsletterFormSignUp.stateless";

import { AmplitudeEvents } from "@/constants/amplitude";
import { PhoneNumberInputHandle } from "@/dashboard-design-system/PhoneNumberInput/types";
import { trpc } from "@/helpers/trpc";
import { useAmplitude } from "@/hooks/amplitude/useAmplitude";
import { NewsletterFormFrontendPageOutput } from "@/types/trpc";

const extractCommunicationPreferenceIds = (
  preferences: NewsletterFormSignupData["communicationPreferences"]
): string[] => {
  if (!Array.isArray(preferences)) {
    return [];
  }

  return preferences
    .map((pref) => (typeof pref === "string" ? pref : pref.value))
    .filter((id): id is string => id !== null && id !== "");
};

export const NewsletterFormSignUp: FC<{
  newsletterForm: NewsletterFormFrontendPageOutput;
  isEmbedded?: boolean;
}> = ({ newsletterForm, isEmbedded }) => {
  const { logAmplitudeEvent } = useAmplitude();

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [phoneInputKey, setPhoneInputKey] = useState(0);

  const { executeTrpc } = useRecaptchaRetry();

  const { mutateAsync: signupToNewsletterForm } = trpc.signupToNewsletterForm.useMutation();

  const form = useForm<NewsletterFormSignupData>({
    mode: "onBlur",
    reValidateMode: "onChange",
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phoneNumber: "",
      language: "",
      address: "",
      zipCode: "",
      city: "",
      state: "",
      country: "",
      communicationPreferences: [],
    },
  });

  const startTime = useRef(Date.now());
  const phoneNumberInputRef = useRef<PhoneNumberInputHandle>(null);

  const onSubmit = async (formData: NewsletterFormSignupData) => {
    setIsSubmitting(true);

    const processedPreferences = extractCommunicationPreferenceIds(formData.communicationPreferences);

    const { error } = await executeTrpc(
      RecaptchaAction.SignUpToNewsletterForm,
      formData.email,
      signupToNewsletterForm,
      {
        newsletterFormId: newsletterForm.id,
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phoneNumber: formData.phoneNumber,
        language: formData.language,
        address: formData.address,
        zipCode: formData.zipCode,
        city: formData.city,
        state: formData.state,
        country: formData.country,
        communicationPreferences: processedPreferences,
      }
    );

    if (error) {
      setIsSubmitting(false);
      return;
    }
    form.reset();
    // Force phone input to remount by changing its key, which clears the library's internal state
    setPhoneInputKey((prev) => prev + 1);

    setIsSubmitting(false);
    setIsSubmitted(true);
    setTimeout(() => setIsSubmitted(false), 5000);

    logAmplitudeEvent(AmplitudeEvents.SignupFormSubmitted, {
      organizationId: newsletterForm.organization.id,
      formId: newsletterForm.id,
      formPath: newsletterForm.path,
      timeElapsed: Date.now() - startTime.current,
      isEmbedded,
    });
  };

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} style={{ width: isEmbedded ? "100%" : "unset" }}>
      <NewsletterFormSignUpStateless
        mainColor={newsletterForm.mainColor}
        buttonColor={newsletterForm.buttonColor}
        title={newsletterForm.title}
        submitState={isSubmitted ? "submitted" : isSubmitting ? "submitting" : "idle"}
        form={form}
        organization={newsletterForm.organization}
        isEmbedded={isEmbedded}
        hasFirstName={newsletterForm.hasFirstName}
        hasLastName={newsletterForm.hasLastName}
        hasPhoneNumber={newsletterForm.hasPhoneNumber}
        hasAddress={newsletterForm.hasAddress}
        hasLanguage={newsletterForm.hasLanguage}
        hasCommunicationPreferences={newsletterForm.hasCommunicationPreferences}
        communicationPreferences={newsletterForm.communicationPreferences}
        phoneNumberInputRef={phoneNumberInputRef}
        phoneInputKey={phoneInputKey}
      />
    </form>
  );
};
